import { useCreateInstallation } from '@/installation/api'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { installationFormSchema } from '@/installFlow/schemas'
import { toast } from 'sonner'
import { StepFrame } from '@/components/installFlow/StepFrame'
import { StepHeader } from '@/components/installFlow/StepHeader'
import { StepFooter } from "@/components/installFlow/StepFooter"
import { useInstallFlowNavigation } from '@/installFlow/useInstallFlowNavigation'
import { useStepSearch } from '@/installFlow/useStepSearch'
import { useNameplateScan } from '@/installFlow/nameplateScan/api'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2 } from 'lucide-react'
import { TaskStatus } from '@/installFlow/nameplateScan/types'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  useCreateManufacturer,
  useCreateModel,
  useManufacturers,
  useModels,
  useLookupModelSpecs,
} from '@/equipment/api'
import { Autocomplete } from '@/components/Autocomplete'
import { ModelSpecsDialog, ModelSpecs } from '@/components/installFlow/ModelSpecsDialog'

const ManufacturerSection = ({ onManufacturerChange }: {
  onManufacturerChange?: () => void
}) => {
  const { control, setValue, watch } = useFormContext()
  const { data: manufacturersData } = useManufacturers()
  const createManufacturer = useCreateManufacturer()

  const manufacturers = manufacturersData?.results || []
  const selectedManufacturerId = watch('manufacturer_id')
  const selectedManufacturerName = watch('manufacturer_name')

  const handleSelect = (manufacturerId: number, manufacturerName: string) => {
    setValue('manufacturer_id', manufacturerId)
    setValue('manufacturer_name', manufacturerName)
    onManufacturerChange?.()
  }

  const handleClear = () => {
    setValue('manufacturer_id', null)
    setValue('manufacturer_name', '')
    onManufacturerChange?.()
  }

  const handleCreate = async (name: string) => {
    try {
      const newManufacturer = await createManufacturer.mutateAsync({ name })
      setValue('manufacturer_id', newManufacturer.id)
      setValue('manufacturer_name', newManufacturer.name)
      onManufacturerChange?.()
    } catch (error) {
      console.error('Failed to create manufacturer:', error)
      toast.error('Failed to create manufacturer')
      throw error
    }
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="manufacturer">Manufacturer *</Label>
      <Controller
        name="manufacturer_id"
        control={control}
        render={({ field }) => (
          <Autocomplete
            items={manufacturers}
            itemsHeading="All manufacturers"
            selectedId={selectedManufacturerId}
            selectedName={selectedManufacturerName}
            onSelect={handleSelect}
            onClear={handleClear}
            onCreate={handleCreate}
            placeholder="Search manufacturers..."
            emptyMessage="No manufacturers found"
            createLabel="Create"
            isCreating={createManufacturer.isPending}
          />
        )}
      />
    </div>
  )
};

const ModelSection = ({ manufacturerId, disabled }: {
  manufacturerId: number | null
  disabled?: boolean
}) => {
  const { control, setValue, watch } = useFormContext()
  const { data: modelsData } = useModels(manufacturerId ?? undefined)

  const currentModelId = watch('model_id')
  const currentModelName = watch('model_name')

  const models = useMemo(() =>
      modelsData?.results?.map(m => ({ id: m.id, name: m.model })) || [],
    [modelsData]
  )

  const handleSelect = (modelId: number, modelName: string) => {
    setValue('model_id', modelId)
    setValue('model_name', modelName)
  }

  const handleClear = () => {
    setValue('model_id', null)
    setValue('model_name', '')
  }

  const handleCreate = async (name: string) => {
    // Don't create model yet - just set the name
    // Model will be created when form is submitted
    setValue('model_id', null)
    setValue('model_name', name)
  }

  return (
    <div className={`space-y-2 transition-opacity ${disabled ? 'opacity-50' : ''}`}>
      <Label htmlFor="model">Model *</Label>
      <Controller
        name="model_id"
        control={control}
        render={({ field }) => (
          <Autocomplete
            items={models}
            itemsHeading="All models"
            selectedId={currentModelId}
            selectedName={currentModelName}
            onSelect={handleSelect}
            onClear={handleClear}
            onCreate={manufacturerId ? handleCreate : undefined}
            placeholder={manufacturerId ? "Search models..." : "Select a manufacturer first"}
            emptyMessage={manufacturerId ? "No models found" : "Select a manufacturer first"}
            createLabel="Create"
            disabled={disabled || !manufacturerId}
          />
        )}
      />
    </div>
  )
};

const EquipmentDetailsSection = () => {
  const { control } = useFormContext()

  return (
    <div className="space-y-2 transition-opacity">
      <Label htmlFor="serial_number">Serial Number</Label>
      <Controller
        name="serial_number"
        control={control}
        render={({ field }) => (
          <Input
            id="serial_number"
            {...field}
          />
        )}
      />
    </div>
  )
};

export function DetailsStep() {
  const { goToStep, goToPreviousStep } = useInstallFlowNavigation()
  const search = useStepSearch('details')
  const locationId = search.locationId
  const sublocationId = search.sublocationId
  const installationName = search.installationName
  const scanId = search.scanId

  const createInstallation = useCreateInstallation()
  const createModel = useCreateModel()
  const lookupSpecs = useLookupModelSpecs()

  // Poll for nameplate scan identification results if scanId exists
  const { data: scan } = useNameplateScan(scanId)

  // Determine if identification is currently processing
  const isIdentificationProcessing = scan &&
    !scan.identification_result &&
    (scan.identification_status === TaskStatus.PENDING || scan.identification_status === TaskStatus.RUNNING)

  const form = useForm({
    resolver: zodResolver(installationFormSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      manufacturer_id: null,
      manufacturer_name: '',
      model_id: null,
      model_name: '',
      serial_number: '',
    },
  })

  // State for model specs lookup dialog
  const [showSpecsDialog, setShowSpecsDialog] = useState(false)
  const [pendingSubmitData, setPendingSubmitData] = useState<{
    manufacturer_id: number
    manufacturer_name: string
    model_name: string
    serial_number: string
  } | null>(null)

  // Track if we've already auto-filled from identification results to prevent re-filling
  const hasAutoFilled = useRef(false)

  // Auto-fill form when identification results arrive
  useEffect(() => {
    if (!scan?.identification_result || hasAutoFilled.current) {
      return
    }

    const identificationResult = scan.identification_result
    const values = form.getValues()

    // Only auto-fill if fields are empty (preserve user edits)
    if (!values.manufacturer_id && !values.manufacturer_name) {
      if (identificationResult.manufacturer_id) {
        form.setValue('manufacturer_id', identificationResult.manufacturer_id)
        form.setValue('manufacturer_name', identificationResult.manufacturer_name || '')
      } else if (identificationResult.manufacturer_name_raw) {
        form.setValue('manufacturer_name', identificationResult.manufacturer_name_raw)
      }
    }

    if (!values.model_id && !values.model_name) {
      if (identificationResult.equipment_model_id) {
        form.setValue('model_id', identificationResult.equipment_model_id)
        form.setValue('model_name', identificationResult.equipment_model_name || '')
      } else if (identificationResult.model_number_raw) {
        form.setValue('model_name', identificationResult.model_number_raw)
      }
    }

    if (!values.serial_number && identificationResult.serial_number) {
      form.setValue('serial_number', identificationResult.serial_number)
    }

    hasAutoFilled.current = true
  }, [scan, form])

  // Helper to create installation with a model ID
  const createInstallationWithModel = async (serialNumber: string, modelId: number) => {
    const newInstallation = await createInstallation.mutateAsync({
      name: installationName!,
      sublocation: sublocationId,
      location: locationId,
      serial_number: serialNumber || undefined,
      equipment_model_id: modelId,
      status: 'active',
    })

    goToStep('link', {
      installationId: newInstallation.id,
    })
  }

  const handleSubmit = async () => {
    const data = form.getValues()

    if (!data.model_id && !data.model_name) {
      console.error('Equipment model is required')
      toast.error('Equipment model is required')
      return
    }

    if (!installationName) {
      console.error('Installation name is required')
      toast.error('Installation name is required')
      return
    }

    // If we have an existing model, proceed directly
    if (data.model_id) {
      try {
        await createInstallationWithModel(data.serial_number, data.model_id)
      } catch (error) {
        console.error('Failed to create installation:', error)
        toast.error('Failed to create installation', {
          description: 'Please try again.',
        })
      }
      return
    }

    // Creating a new model - show specs dialog and trigger lookup
    if (!data.manufacturer_id) {
      toast.error('Manufacturer is required to create a model')
      return
    }

    const submitData = {
      manufacturer_id: data.manufacturer_id,
      manufacturer_name: data.manufacturer_name,
      model_name: data.model_name,
      serial_number: data.serial_number,
    }

    setPendingSubmitData(submitData)
    setShowSpecsDialog(true)

    // Trigger the specs lookup
    lookupSpecs.mutate({
      manufacturer_name: data.manufacturer_name,
      model_name: data.model_name,
    })
  }

  // Handle specs dialog confirmation - create model with specs and installation
  const handleSpecsConfirm = async (specs: ModelSpecs) => {
    if (!pendingSubmitData) return

    try {
      // Create model with specs
      const newModel = await createModel.mutateAsync({
        name: pendingSubmitData.model_name,
        model: pendingSubmitData.model_name,
        manufacturer_id: pendingSubmitData.manufacturer_id,
        asset_type: specs.asset_type,
        phase_count: specs.phase_count ?? undefined,
        refrigerant_type: specs.refrigerant_type ?? undefined,
        nameplate_capacity: specs.nameplate_capacity_btu
          ? specs.nameplate_capacity_btu / 1000  // BTU to MBH
          : undefined,
      })

      // Create installation
      await createInstallationWithModel(pendingSubmitData.serial_number, newModel.id)
    } catch (error) {
      console.error('Failed to create equipment:', error)
      toast.error('Failed to create equipment', {
        description: 'Please try again.',
      })
    } finally {
      setShowSpecsDialog(false)
      setPendingSubmitData(null)
      lookupSpecs.reset()
    }
  }

  // Handle dialog cancel - user wants to change model
  const handleDialogCancel = () => {
    setShowSpecsDialog(false)
    setPendingSubmitData(null)
    lookupSpecs.reset()
  }

  return (
  <>
    <StepFrame
      header={
        <StepHeader
          title="Equipment Details"
          onBack={goToPreviousStep}
        />
      }
      main={
        <div className="space-y-4">
          {isIdentificationProcessing && (
            <Alert variant="default">
              <div className="flex items-center gap-3">
                <Loader2 className="h-4 w-4 animate-spin"/>
                <AlertDescription>
                  Analyzing nameplate details...
                </AlertDescription>
              </div>
            </Alert>
          )}

          <Card>
            <FormProvider {...form}>
              <form onSubmit={handleSubmit}>
                <div className="p-6">
                  <ManufacturerSection
                    onManufacturerChange={() => {
                      form.setValue('model_id', null)
                    }}
                  />
                </div>

                <hr className="border-dashed"/>

                <div className="p-6">
                  <ModelSection
                    manufacturerId={form.watch('manufacturer_id')}
                    disabled={!form.watch('manufacturer_id')}
                  />
                </div>

                <hr className="border-dashed"/>

                <div className="p-6">
                  <EquipmentDetailsSection/>
                </div>
              </form>
            </FormProvider>
          </Card>
        </div>
      }
      footer={
        <StepFooter actions={[
          <Button
            type="button"
            size="lg"
            className="w-full"
            onClick={handleSubmit}
            disabled={
              createInstallation.isPending ||
              createModel.isPending ||
              showSpecsDialog ||
              (!form.watch('model_id') && !form.watch('model_name')) ||
              !installationName
            }
          >
            {createInstallation.isPending || createModel.isPending ? 'Creating...' : 'Save & Continue'}
          </Button>
        ]}/>
      }
    />

    {/* Model Specs Lookup Dialog */}
    <ModelSpecsDialog
      open={showSpecsDialog}
      onOpenChange={handleDialogCancel}
      manufacturerName={pendingSubmitData?.manufacturer_name || ''}
      modelName={pendingSubmitData?.model_name || ''}
      isLoading={lookupSpecs.isPending}
      lookupResult={lookupSpecs.data}
      lookupFailed={lookupSpecs.isError}
      onConfirm={handleSpecsConfirm}
    />
  </>
  )
}